import { computed, DestroyRef, Directive, effect, ElementRef, inject, input, InputSignal, Signal, signal, WritableSignal } from '@angular/core'
import { AbstractControl, FormGroup, Validators } from '@angular/forms'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AccordionValue } from 'primeng/types/accordion'
import { DetailviewNavigationService, registerNavPanels } from '@services/detailview-navigation.service'
import { OverrideStateService } from '@services/override-state.service'
import { PermissionService } from '@app/core/auth/services/permission.service'
import { CategoryService } from '@app/core/auth/services/category.service'
import { hasActualValue, isAlbertValueObjectForm, requiredWithoutExternalValue } from '@core/utils/form-utils'
import { AlbertValueObjectForm } from '../models/member/member.model'
import { PolicyStatusType } from '../models/policy/policy.model'

/**
 * Shared behaviour of the insured detailview's form components: edit rights, which override fields are
 * shown, toggling them, and the accordion panels opened by the section navigation.
 *
 * @example
 * export class AviationHullFormComponent extends InsuredBaseForm<AviationHullForm> { ... }
 */
@Directive()
export abstract class InsuredBaseForm<T extends { [K in keyof T]: AbstractControl<unknown, unknown> }> {
  /** Form group the component edits, owned by the insured detailview */
  public abstract readonly form: InputSignal<FormGroup<T>>
  /** Whether the insured is being created; everything is editable then */
  public readonly isCreateMode = input.required<boolean>()
  /** External id of the insured; `null` for an insured that only exists in Albert */
  public readonly memberExternalId = input<string | null | undefined>()
  /** Status of the policy; a POLICY is read-only, a PROPOSAL editable */
  public readonly policyStatus = input<PolicyStatusType | null | undefined>()
  /** Opens all accordion panels of the component */
  public readonly expandAllPanels = input<boolean>(false)
  /** Warning shown when override fields are filled manually but no Origami data exists for the same coverage. */
  public readonly OVERRIDE_WITHOUT_ORIGAMI_WARNING =
    'Data mismatch: No data found in Origami for this coverage. Delete override fields or update coverage in Origami.'
  /** Accordion values of the component's open panels */
  public readonly activePanels = signal<string[]>([])
  protected readonly overrideService = inject(OverrideStateService)
  protected readonly permissionService = inject(PermissionService)
  protected readonly categoryService = inject(CategoryService)
  /** Whether the user may edit: needs edit rights for the active category, and the policy must not be a POLICY */
  public readonly canEdit = computed(() => {
    const cat = this.categoryService.activeCategory()
    const canEdit = this.permissionService.canEdit(cat)
    if (canEdit) {
      if (this.isCreateMode()) return true
      if (this.memberExternalId() === null) return true
      if (this.policyStatus() === 'POLICY') return false
      if (this.policyStatus() === 'PROPOSAL') return true
    }

    // Default to permission check if status is missing or other
    return cat === null ? false : this.permissionService.canEdit(cat)
  })
  protected readonly destroyRef = inject(DestroyRef)
  protected readonly manuallyExpandedFields = signal<Set<string>>(new Set())

  private static hasAnyLocalOverride(group: AbstractControl): boolean {
    if (!(group instanceof FormGroup)) return false
    for (const key of Object.keys(group.controls)) {
      const ctrl = group.get(key)
      if (!ctrl) continue
      if (isAlbertValueObjectForm(ctrl)) {
        if (hasActualValue(ctrl.controls.localValue.value)) {
          return true
        }
      } else if (ctrl instanceof FormGroup) {
        if (InsuredBaseForm.hasAnyLocalOverride(ctrl)) {
          return true
        }
      }
    }
    return false
  }

  private static hasAnyExternalValue(group: AbstractControl): boolean {
    if (!(group instanceof FormGroup)) return false
    for (const key of Object.keys(group.controls)) {
      const ctrl = group.get(key)
      if (!ctrl) continue
      if (isAlbertValueObjectForm(ctrl)) {
        if (hasActualValue(ctrl.controls.externalValue.value)) {
          return true
        }
      } else if (ctrl instanceof FormGroup) {
        if (InsuredBaseForm.hasAnyExternalValue(ctrl)) {
          return true
        }
      }
    }
    return false
  }

  /**
   * Checks for overrides on a synced insured whose coverage has no Origami data, which shows
   * `OVERRIDE_WITHOUT_ORIGAMI_WARNING`.
   *
   * @returns True if any override is filled while the form has no Origami value and no external id
   */
  public hasOverrideWithoutOrigamiData(): boolean {
    if (this.isCreateMode()) return false
    if (!hasActualValue(this.memberExternalId())) return false

    const group = this.getFormGroup() as unknown as FormGroup | null
    if (!group) return false

    const externalIdCtrl = group.get('externalId')
    if (externalIdCtrl && hasActualValue(externalIdCtrl.value)) return false

    return (InsuredBaseForm.hasAnyLocalOverride(group) && !InsuredBaseForm.hasAnyExternalValue(group))
  }

  /**
   * Decides whether a field's override input is shown: with "show all", when expanded by hand, when
   * required, or when it holds a value.
   *
   * @param field - Path of the field in the form group, e.g. `general.periodFrom`
   * @returns True if the override input is shown
   */
  public isFieldVisible(field: string): boolean {
    if (this.overrideService.showAll()) return true
    if (this.manuallyExpandedFields().has(field)) return true
    if (this.isLocalValueRequired(field)) return true

    const control = this.getFormGroup().get(field)
    if (isAlbertValueObjectForm(control)) {
      return hasActualValue(control.controls.localValue.value)
    }
    return hasActualValue(control?.value)
  }

  /**
   * Checks whether a field's override must be filled: always required, or required while Origami has no value.
   *
   * @param field - Path of the field in the form group
   * @returns True if the override is required
   */
  public isLocalValueRequired(field: string): boolean {
    const control = this.getFormGroup().get(field)
    if (!isAlbertValueObjectForm(control)) return false

    const localValue = control.controls.localValue
    if (localValue.hasValidator(Validators.required)) return true

    return localValue.hasValidator(requiredWithoutExternalValue)
      && !hasActualValue(control.controls.externalValue.value)
  }

  /**
   * Shows or hides a field's override input; hiding it clears the override. Does nothing without edit rights.
   *
   * @param field - Path of the field in the form group
   */
  public toggleField(field: string): void {
    if (!this.canEdit()) return

    const control = this.getFormGroup().get(field)
    if (isAlbertValueObjectForm(control)) {
      if (this.isFieldVisible(field)) {
        if (hasActualValue(control.controls.localValue.value)) {
          control.controls.localValue.markAsDirty()
        }
        control.controls.localValue.setValue(null)
        this.manuallyExpandedFields.update(prev => {
          const next = new Set(prev)
          next.delete(field)
          return next
        })
      } else {
        control.controls.localValue.setValue(null)
        this.manuallyExpandedFields.update(prev => {
          const next = new Set(prev)
          next.add(field)
          return next
        })
      }
    }
  }

  /** Form group the visibility and toggle helpers work on. */
  protected abstract getFormGroup(): FormGroup<T>

  /**
   * Couples a value object's `checked` control with a signal: unchecking clears the override, a
   * patched override checks it. Call from `ngOnInit`; the subscriptions end with the component.
   *
   * @param group - Value object with a `checked` control
   * @param toggleSignal - Signal mirroring the checked state for the template
   */
  protected setupToggleLogic<V>(group: AlbertValueObjectForm<V>, toggleSignal: WritableSignal<boolean>): void {
    // Initial state
    toggleSignal.set(group.controls.checked?.value !== false)
    group.controls.checked?.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(checked => {
      toggleSignal.set(checked !== false)
      if (checked === false && hasActualValue(group.controls.localValue.value)) {
        group.controls.localValue.setValue(null)
      }
      // Validators like `requiredWhenChecked` depend on the toggle, so re-run them when it flips.
      group.controls.localValue.updateValueAndValidity()
    })

    // Sync checked state when localValue is patched (e.g. from API)
    group.controls.localValue.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(val => {
      const hasVal = hasActualValue(val)
      if (hasVal && group.controls.checked?.value === false) {
        group.controls.checked?.setValue(true)
      }
    })

    // Initial sync
    if (hasActualValue(group.controls.localValue.value) && ((group.controls.checked?.value) === false)) {
      group.controls.checked?.setValue(true)
    }
  }

  /**
   * Takes over the open panels from the accordion.
   *
   * @param value - Accordion value as emitted by PrimeNG
   */
  protected onActivePanelsChange(value: AccordionValue): void {
    if (value === null) {
      this.activePanels.set([])
      return
    }
    this.activePanels.set(Array.isArray(value) ? value.map(String) : [String(value)])
  }

  /**
   * Keeps the accordion's open panels in line with the detailview: opens all panels while
   * `expandAllPanels` is set, otherwise opens the panel the section navigation jumps to.
   * Call from the constructor.
   *
   * @param nav - Navigation service of the detailview
   * @param mainIndex - Position of this coverage in the detailview's sections
   * @param panelValues - Accordion values of all panels; the first one is opened initially
   * @param ownPanels - Scroll targets in this component's own template, registered with the navigation
   */
  protected setupPanelNavigationEffects(
    nav: DetailviewNavigationService,
    mainIndex: Signal<number | undefined>,
    panelValues: readonly string[],
    ownPanels?: Signal<readonly ElementRef<HTMLElement>[]>
  ): void {
    this.activePanels.set(panelValues.slice(0, 1))

    effect(() => {
      if (this.expandAllPanels()) {
        this.activePanels.set([...panelValues])
        return
      }

      const target = nav.subSectionTarget()
      if (target && target.mainIndex === mainIndex()) {
        this.activePanels.update(prev => {
          if (!prev.includes(target.subValue)) {
            return [...prev, target.subValue]
          }
          return prev
        })
      }
    })

    if (ownPanels) registerNavPanels(nav, ownPanels)
  }
}
